// pages/Customers.js
import React, { useState } from 'react';
import { Button, Input, Modal, Select, TextArea } from '../common';
import { Customer } from '../../models';
import { useTranslations } from '../../hooks/useTranslations';
import { useCustomersPG } from '../../hooks/useCustomersPG';
import { GenderToggleSelector } from '../customer/GenderToggleSelector';  

// Customer Modal Component (for both Add and Edit)
export function CustomerModal({ isOpen, onClose, onSubmit, customer = null, mode = 'add' }) {
  const [formData, setFormData] = useState({
    full_name: '',
    gender: 'female',
    phone: '',
    email: '',
    address: '',
    birth_date: '',
    skin_type: '',
    allergies: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { deleteCustomer } = useCustomersPG();

  // Reset form when modal opens/closes or customer changes
  // Find this useEffect in CustomerModal.js and replace it with this version:

React.useEffect(() => {
  if (isOpen) {
    if (customer && mode === 'edit') {
      // ADD DEBUGGING: Log the customer data being received
      console.log('CustomerModal Edit Mode - Full customer object:', customer);
      console.log('CustomerModal Edit Mode - Birth date field:', {
        birth_date: customer.birth_date,
        birth_date_type: typeof customer.birth_date,
        birth_date_value: customer.birth_date,
        all_date_fields: {
          birth_date: customer.birth_date,
          created_at: customer.created_at,
          updated_at: customer.updated_at
        }
      });

      // Format birth_date if it's a Date object or timestamp
      let formattedBirthDate = '';
      if (customer.birth_date) {
        if (customer.birth_date instanceof Date) {
          // It's a Date object - format to YYYY-MM-DD
          formattedBirthDate = customer.birth_date.toISOString().split('T')[0];
          console.log('CustomerModal: Converted Date object to:', formattedBirthDate);
        } else if (typeof customer.birth_date === 'string') {
          // It's already a string - check if it needs formatting
          if (customer.birth_date.includes('T')) {
            // It's an ISO string, extract date part
            formattedBirthDate = customer.birth_date.split('T')[0];
            console.log('CustomerModal: Converted ISO string to:', formattedBirthDate);
          } else {
            // It's already in YYYY-MM-DD format
            formattedBirthDate = customer.birth_date;
            console.log('CustomerModal: Using existing date format:', formattedBirthDate);
          }
        } else {
          console.warn('CustomerModal: Unexpected birth_date format:', typeof customer.birth_date, customer.birth_date);
          formattedBirthDate = '';
        }
      }

      // Populate form with existing customer data
      setFormData({
        full_name: customer.full_name || '',
        gender: customer?.gender || 'female',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        birth_date: formattedBirthDate, // Use formatted date
        skin_type: customer.skin_type || '',
        allergies: customer.allergies || ''
      });

      // ADD DEBUGGING: Log what we set in the form
      console.log('CustomerModal: Set form data:', {
        birth_date: formattedBirthDate,
        full_name: customer.full_name,
        phone: customer.phone
      });
    } else {
      // Reset form for new customer
      setFormData({
        full_name: '',
        gender: 'female',
        phone: '',
        email: '',
        address: '',
        birth_date: '',
        skin_type: '',
        allergies: ''
      });
    }
    setErrors({});
    setSubmitSuccess(false);
  }
}, [isOpen, customer, mode]);

  const t = useTranslations('customerModal');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDelete = async () => {
    if (!customer) return;
    
    setIsDeleting(true);
    try {
      await deleteCustomer(customer.id, { 
        confirmDeletion: true,
        forceDeleteWithData: true // Allow deletion even if customer has data
      });
      
      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error deleting customer:', error);
      setErrors({ submit: error.message || 'Failed to delete customer' });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const validateForm = () => {
    const validation = Customer.validate(formData);
    const fieldErrors = {};

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        if (error.includes('Full name')) fieldErrors.full_name = error;
        if (error.includes('Phone')) fieldErrors.phone = error;
        if (error.includes('email')) fieldErrors.email = error;
        if (error.includes('skin type')) fieldErrors.skin_type = error;
      });
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (mode === 'edit') {
        // Update existing customer using hook
        await onSubmit(customer.id, formData, 'edit');
      } else {
        // Create new customer using hook
        await onSubmit(formData, 'add');
      }
      
      setSubmitSuccess(true);
      
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const skinTypeOptions = Customer.getSkinTypes();

  const modalTitle = mode === 'edit' ? t.editCustomer : t.addNewCustomer;
  const successMessage = mode === 'edit' ? t.customerUpdatedSuccessfully : t.customerAddedSuccessfully;
  const successDescription = mode === 'edit' ? t.customerInformationHasBeenUpdated : t.newCustomerHasBeenAddedToDatabase;
  const submitButtonText = mode === 'edit' 
    ? (isSubmitting ? t.updatingCustomer : t.updateCustomer)
    : (isSubmitting ? t.addingCustomer : t.addCustomer);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={modalTitle}
      size="medium"
    >
      {submitSuccess ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{successMessage}</h3>
          <p className="text-gray-600">{successDescription}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Show customer info header for edit mode */}
          {mode === 'edit' && customer && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center ml-3">
                  <span className="text-pink-600 font-semibold text-lg">
                    {customer.full_name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{customer.full_name}</h4>
                  <p className="text-sm text-gray-500">{t.customerSince} {new Date(customer.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}
          

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0 text-red-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.submit}</p>
                </div>
              </div>
            </div>
          )}

            {/* Gender Toggle Selector */}
            <GenderToggleSelector
              value={formData.gender}
              onChange={(gender) => setFormData(prev => ({ ...prev, gender }))}
              error={errors.gender}
              t={{
                gender: 'מגדר',
                female: 'נקבה',
                male: 'זכר'
              }}
            />
            {/* <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.gender}
            </label>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  formData.gender === 'female'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t.female}
              </button>
              <button
                type="button"
                onClick={}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  formData.gender === 'male'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {t.male}
              </button>
            </div>
          </div> */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label={t.fullName}
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder={t.enterCustomerFullName}
                required
                error={errors.full_name}
                leftIcon="👤"
              />
            </div>

            <Input
              label={t.phoneNumber}
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="0501234567"
              required
              error={errors.phone}
              leftIcon="📞"
            />

            <Input
              label={t.emailAddress}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="customer@email.com"
              error={errors.email}
              leftIcon="✉️"
            />

            <Input
              label={t.address}
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder={t.addressPlaceholder}
              error={errors.address}
              leftIcon="📍"
            />

            <Input
              label={t.birthDate}
              name="birth_date"
              type="date"
              value={formData.birth_date}
              onChange={handleInputChange}
              error={errors.birth_date}
              leftIcon="🎂"
              max={new Date().toISOString().split('T')[0]} // Prevent future dates
            />

            {/* <div className="md:col-span-2">
              <Select
                label={t.skinType}
                name="skin_type"
                value={formData.skin_type}
                onChange={handleInputChange}
                options={skinTypeOptions}
                placeholder="Select skin type (optional)"
                error={errors.skin_type}
              />
            </div> */}

            <div className="md:col-span-2">
              <TextArea
                label={t.allergiesAndSensitivities}
                name="allergies"
                value={formData.allergies}
                onChange={handleInputChange}
                placeholder={t.listAnyKnownAllergiesOrSensitivities}
                rows={3}
              />
            </div>
          </div>

          <div className="flex items-center justify-between space-x-3 pt-6 border-t border-gray-200">
            <div>
              {mode === 'edit' && (
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting || isDeleting}
                  className="mx-2"
                >
                  {isDeleting ? t.deleting : t.delete}
                </Button>
              )}
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting || isDeleting}
                className='mx-2'
              >
                {t.cancel}
              </Button>
              <Button
                onClick={handleSubmit}
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting || isDeleting}
              >
                {submitButtonText}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">מחיקת לקוח</h3>
            <p className="text-gray-600 mb-6">
              האם אתה בטוח שברצונך למחוק את הלקוח "{customer?.full_name}"? פעולה זו תמחק לצמיתות את כל הנתונים של הלקוח ולא ניתן יהיה לבטל אותה.
            </p>
            <div className="flex space-x-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
                className="mx-2"
              >
                {t.cancel}
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? t.deleting : t.delete}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
